/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { useBreakpoint } from 'sarsaparilla';

import type {
    FlexDateFilters,
    FlexDatePickerPopoverAlign,
    FlexDatePickerSize,
} from './FlexDatePicker.types';
import { DatePickerContent } from './DatePickerContent';
import { DatePickerDrawer } from './DatePickerDrawer';
import { DatePickerPopover } from './DatePickerPopover';
import { useDatePickerDraft } from './useDatePickerDraft';
import styles from './FlexDatePicker.module.scss';

export interface FlexDatePickerProps {
    value: FlexDateFilters;
    onChange: (value: FlexDateFilters) => void;
    isFlexibleAllowed: boolean;
    /** GA event category for the picker's click tracking. */
    clickTagCategory?: string;
    /** Trigger height. Defaults to `md`. */
    size?: FlexDatePickerSize;
    /** Popover alignment. Defaults to `center`. */
    popoverAlign?: FlexDatePickerPopoverAlign;
}

export function FlexDatePicker({
    value = {},
    onChange,
    isFlexibleAllowed = false,
    clickTagCategory = 'Search',
    size = 'md',
    popoverAlign = 'center',
}: FlexDatePickerProps) {
    // The draft lives here, not in the wrappers, so it survives a resize that
    // swaps the popover for the drawer.
    const draft = useDatePickerDraft({
        filters: value,
        setFilters: onChange,
        isFlexibleAllowed,
    });
    const { isSmAndDown } = useBreakpoint();
    const Wrapper = isSmAndDown ? DatePickerDrawer : DatePickerPopover;
    // The legacy picker for searches without flexible dates sent no events.
    const trackingCategory = isFlexibleAllowed ? clickTagCategory : undefined;

    return (
        <div className={styles.FlexDatePicker}>
            <Wrapper
                shell={draft}
                clickTagCategory={trackingCategory}
                size={size}
                popoverAlign={popoverAlign}
            >
                <DatePickerContent
                    variant={isSmAndDown ? 'drawer' : 'popover'}
                    isFlexibleAllowed={isFlexibleAllowed}
                    clickTagCategory={trackingCategory}
                    state={draft.state}
                    setState={draft.setState}
                    onClearDates={draft.onClearDates}
                    hasDates={draft.hasDates}
                    closeAction={draft.closeAction}
                    onApply={() => draft.onOpenChange(false)}
                />
            </Wrapper>
        </div>
    );
}
